import { useQuery } from '@tanstack/react-query';
import { fetchHealth, HEALTH_STATES } from '../services/health';

/** Steady-state cadence while everything is fine. */
export const HEALTH_POLL_MS = 30000;

/** Tighter cadence while something is wrong, so recovery is noticed quickly. */
export const HEALTH_POLL_WHEN_UNHEALTHY_MS = 10000;

export const HEALTH_QUERY_KEY = ['backend-health'];

/**
 * Chooses the poll cadence from the last result. Anything that is not healthy —
 * including the not-yet-known state before the first poll resolves — polls faster.
 */
export const healthRefetchInterval = (data) =>
  data?.state === HEALTH_STATES.HEALTHY ? HEALTH_POLL_MS : HEALTH_POLL_WHEN_UNHEALTHY_MS;

/**
 * Polls backend health for the header indicator.
 *
 * `refetchIntervalInBackground` is left at its default of false so the timer pauses
 * while the tab is unfocused — idle tabs must not poll all day.
 */
export const useBackendHealth = () => {
  const { data, isLoading } = useQuery({
    queryKey: HEALTH_QUERY_KEY,
    queryFn: fetchHealth,
    refetchInterval: healthRefetchInterval,
    refetchIntervalInBackground: false,
    // Deliberately overrides the app-wide `refetchOnWindowFocus: false` in index.jsx:
    // a returning user must see current truth, not a minute-old green dot.
    refetchOnWindowFocus: true,
    // fetchHealth never rejects, so retries would only delay an already-known answer.
    retry: false,
    staleTime: 0,
  });

  return { health: data, isChecking: isLoading };
};
